import React, { Component, ReactNode } from 'react'
import * as Sentry from '@sentry/react'

import s from './ErrorBoundary.module.css'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  showErrorDetails?: boolean
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
  showDetails: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      showDetails: false,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    })

    Sentry.withScope((scope) => {
      // Add React-specific context
      scope.setContext('react', {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.constructor.name,
      })

      // Add page context
      scope.setContext('page', {
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        referrer: document.referrer,
      })

      // Add user agent and browser info
      scope.setContext('browser', {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
      })

      // Add viewport info
      scope.setContext('viewport', {
        width: window.innerWidth,
        height: window.innerHeight,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
      })

      // Set fingerprint for better error grouping
      scope.setFingerprint(['react-error-boundary', error.name, error.message])

      // Set error level
      scope.setLevel('error')

      Sentry.captureException(error)
    })

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo)

    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      showDetails: false,
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  toggleDetails = () => {
    this.setState({ showDetails: !this.state.showDetails })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className={s.errorContainer}>
          <h2 className={s.errorTitle}>Something went wrong</h2>
          <p className={s.errorMessage}>
            An unexpected error occurred. You can try refreshing the page or
            contact support if the problem persists.
          </p>

          <div className={s.errorActions}>
            <button className={s.retryButton} onClick={this.handleRetry}>
              Try Again
            </button>
            <button className={s.reloadButton} onClick={this.handleReload}>
              Reload Page
            </button>
          </div>

          {(this.props.showErrorDetails ||
            process.env.NODE_ENV === 'development') && (
            <>
              <button className={s.toggleButton} onClick={this.toggleDetails}>
                {this.state.showDetails ? 'Hide' : 'Show'} Error Details
              </button>

              {this.state.showDetails && (
                <div className={s.errorDetails}>
                  <strong>Error:</strong> {this.state.error?.message}
                  {this.state.error?.stack && (
                    <>
                      <br />
                      <br />
                      <strong>Stack Trace:</strong>
                      <br />
                      {this.state.error.stack}
                    </>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <>
                      <br />
                      <br />
                      <strong>Component Stack:</strong>
                      <br />
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
